<template>
  <div :class="['cf-nav-bar']">
    <div class="cf-nav-bar__container">
      <div
        v-if="!isCategoryCardLayout"
        class="cf-flex cf-flex--row cf-flex--gap-10 cf-max-width-fill-available"
      >
        <p class="cf-nav-bar__title">
          Categories:
        </p>

        <div class="cf-nav-bar__nav">
          <button
            ref="categoriesNavBarScrollButton"
            :class="['cf-nav-bar__button cf-nav-bar__button--previous', { 'cf-nav-bar__button--hide': isNavbarAtStart }]"
            type="button"
            tabindex="-1"
            @click="handleArrowClick('previous')"
          >
            <span class="cf-icon fa-chevron-left" />
          </button>

          <button
            :class="['cf-nav-bar__button cf-nav-bar__button--next', { 'cf-nav-bar__button--hide': isNavbarAtEnd }]"
            type="button"
            tabindex="-1"
            @click="handleArrowClick('next')"
          >
            <span class="cf-icon fa-chevron-right" />
          </button>

          <div
            ref="categoriesNavBar"
            class="cf-nav-bar__nav-items"
            @scroll="handleNavbarTrackPadScroll"
          >
            <button
              v-for="category in menu.filteredCategories"
              :id="`category-nav-button-${ category.categoryId}`"
              :key="category.categoryId"
              type="button"
              :class="['cf-nav-item-button', {
                'cf-nav-item-button--active': selectedCategoryId === category.categoryId,
                'cf-nav-item-button--hidden': category.statementTemplate == 'HiddenTemplate'
              }]"
              tabindex="-1"
              @click="handleClickToScrollToCategory(category.categoryId)"
            >
              {{ category.displayName }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="isFilterByItemAttributesEnabled || isMenuSearchEnabled"
        class="cf-nav-bar__search"
      >
        <button
          v-if="isFilterByItemAttributesEnabled"
          type="button"
          class="cf-button cf-button--tertiary cf-button--smedium"
          title="Filter Menu"
          @click="openMenuListFilterClickHandler"
        >
          Filter
        </button>

        <cf-search-input
          v-if="isMenuSearchEnabled"
          ref="searchInputRef"
          :model-value="searchTerm"
          @update:model-value="handleSearchInputChange"
          @search-focus="searchSuggestionToggler"
        />
      </div>
    </div>

    <transition name="fade-down-only-in">
      <div
        v-if="isMenuSearchEnabled && isSearchActive && suggestedSearchTerms.length > 0"
        class="cf-menu-list-suggested-terms"
      >
        <div class="cf-card cf-card--background-gray cf-flex cf-flex--column">
          <div
            class="cf-suggested-terms-close-button"
            @click="searchSuggestionToggler(false)"
          >
            <button
              type="button"
              title="Close"
              class="cursor-pointer"
            >
              <span
                aria-hidden="true"
                class="cf-icon cf-icon--black cf-icon--small far fa-times"
              />

              <span class="ada-text">
                Close
              </span>
            </button>
          </div>

          <div class="cf-flex cf-flex--column cf-flex--gap-12">
            <h4 class="cf-title cf-title--4">
              {{ menuSearchContent }}
            </h4>

            <div class="cf-flex cf-flex--row cf-flex--wrap cf-flex--gap-16">
              <button
                v-for="suggestedTerm in suggestedSearchTerms"
                :key="suggestedTerm.id"
                class="cf-button cf-button--secondary cf-button--sm cf-button--fit-content"
                type="button"
                @click="setSuggestedTermHandler(suggestedTerm.name)"
              >
                {{ suggestedTerm.name }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfSearchInput from '@/view/components/shared/cf-search-input/index.vue';


  export default {
    name: 'CfMenuListFilter',

    components: {
      CfSearchInput
    },

    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      },

      searchTerm: {
        type: String,
        default: ''
      },

      isCategoryCardLayout: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        selectedCategoryId: null,
        autoScrollingTimeout: null,
        isNavbarAtEnd: false,
        isNavbarAtStart: true,
        categoryNodes: [],
        isAutoScrolling: false,
        mainHeaderHeight: 95,
        navBarHeight: 72,
        isSearchActive: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('menu', ['filterAttributes', 'menu']),
      ...mapGetters('merchant', ['merchant']),

      totalHeaderHeight() {
        let height = this.navBarHeight;

        if (!this.isReadOnly) {
          height += this.mainHeaderHeight;
        }
        return height;
      },

      isMenuSearchEnabled() {
        return this.merchant.features['enable-menu-search'];
      },

      isFilterByItemAttributesEnabled() {
        /** all available menu item attributes that are available for filtering */
        return this.filterAttributes.all.length > 0;
      },

      suggestedSearchTerms() {
        const searchSuggestions = this.filterAttributes.all.filter(attr => attr.isSearchSuggestion);

        if (searchSuggestions) {
          return searchSuggestions.map(suggestion => ({ id: suggestion.id, name: suggestion.name }));
        }

        return [];
      },

      menuSearchContent() {
        return this.content.MenuSearch?.metadata?.searchSuggestion;
      }
    },

    watch: {
      selectedCategoryId: 'onWatchSelectedCategoryId',
      'menu.filteredCategories': 'menuUpdatedHandler'
    },

    mounted() {
      this.onComponentMounted();
    },

    unmounted() {
      this.onComponentDestroyed();
    },

    methods: {
      onComponentMounted() {
        if (!this.isCategoryCardLayout) {
          this.menuUpdatedHandler();

          window.addEventListener('resize', this.resizeEventHandler);
          window.addEventListener('scroll', this.scrollEventHandler);
        }
      },

      onComponentDestroyed() {
        window.removeEventListener('resize', this.resizeEventHandler);
        window.removeEventListener('scroll', this.scrollEventHandler);
      },

      openMenuListFilterClickHandler() {
        this.$openModal('menuListFilterModal');
      },

      searchSuggestionToggler(isSearchActive = true) {
        this.isSearchActive = isSearchActive;
      },

      deActivateSearchHandler() {
        const searchStatus = false;

        this.$emit('set-search-mode', searchStatus);
      },

      handleSearchInputChange(value) {
        this.$emit('update:searchTerm', value);
      },

      setSuggestedTermHandler(suggestedSearchTerm) {
        this.$emit('update:searchTerm', suggestedSearchTerm);
      },

      handleArrowClick(scrollDirection) {
        let distance = Math.ceil(this.$refs.categoriesNavBar.clientWidth * 0.6);

        if (scrollDirection === 'previous') {
          distance *= -1;
        }

        this.$refs.categoriesNavBar.scrollBy({ left: distance, behavior: 'smooth' });
      },

      async handleNavbarTrackPadScroll() {
        await this.$nextTick();

        const scroller = this.$refs.categoriesNavBar;

        if (!scroller) {
          this.isNavbarAtStart = true;
          this.isNavbarAtEnd = true;
          return;
        }

        this.isNavbarAtStart = scroller.scrollLeft <= 0;
        /** NOTE: The +2 pixels is necessary to fix weird behavior where the scroller doesn't scroll exactly to the end  */
        this.isNavbarAtEnd = scroller.scrollWidth - scroller.scrollLeft <= scroller.clientWidth + 2;
      },

      handleClickToScrollToCategory(categoryId) {
        clearTimeout(this.autoScrollingTimeout);

        if (this.selectedCategoryId !== categoryId) {
          this.isAutoScrolling = true;
        }

        this.selectedCategoryId = categoryId;

        const category = document.querySelector(`#category-${categoryId}`);
        const categoryRect = category.getBoundingClientRect();
        const categoryTop = window.pageYOffset + categoryRect.top - this.totalHeaderHeight;

        window.scrollTo({ top: categoryTop, left: 0, behavior: 'smooth' });

        this.autoScrollingTimeout = setTimeout(() => {
          this.isAutoScrolling = false;
        }, 1500);

        this.$emit('scroll-to-category', { categoryId, doNotClose: true });
      },

      onWatchSelectedCategoryId() {
        if (!this.isCategoryCardLayout) {
          const scroller = this.$refs.categoriesNavBar;
          const activeCategoryNavButton = this.$refs.categoriesNavBar.querySelector(`#category-nav-button-${this.selectedCategoryId}`);
          const navScrollButton = this.$refs.categoriesNavBarScrollButton;

          if (!this.selectedCategoryId || !activeCategoryNavButton) {
            return;
          }

          const scrollDistance = activeCategoryNavButton.offsetLeft - navScrollButton.clientWidth - 30;

          scroller.scrollTo({ left: scrollDistance, behavior: 'smooth' });
        }
      },

      async menuUpdatedHandler() {
        await this.$nextTick();

        if (this.menu.filteredCategories.length > 0) {
          const matchingCategory = this.menu.filteredCategories.find(category => category.categoryId === this.selectedCategoryId);

          if (!matchingCategory) {
            this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
          }
        }

        this.categoryNodes = this.$parent.$el.querySelectorAll('.cf-menu-list-target');

        this.scrollEventHandler();

        this.handleNavbarTrackPadScroll();
      },

      resizeEventHandler() {
        this.handleNavbarTrackPadScroll();
      },

      scrollEventHandler() {
        /* NOTE: This is not unit tested yet due to this being a bit experimental and likely to change */

        if (this.menu.filteredCategories.length === 0 || this.isAutoScrolling) {
          return;
        }

        const isAtBottomOfPage = (window.innerHeight + window.scrollY) >= document.body.scrollHeight;

        const isAtTopOfPage = window.scrollY <= 0;

        if (isAtTopOfPage) {
          this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
          return;
        }

        if (isAtBottomOfPage) {
          this.selectedCategoryId = this.menu.filteredCategories[this.menu.filteredCategories.length - 1].categoryId;
          return;
        }

        let activeNode;

        const referencePoint = window.pageYOffset + this.totalHeaderHeight + 160;

        for (let i = 0; i < this.categoryNodes.length; i++) {
          const rect = this.categoryNodes[i].getBoundingClientRect();
          const top = rect.top + window.pageYOffset;
          const bottom = rect.bottom + window.pageYOffset;

          if (top < referencePoint && bottom > referencePoint) {
            activeNode = this.categoryNodes[i];
          }
        }

        if (activeNode) {
          this.selectedCategoryId = Number(activeNode.getAttribute('id').replace('category-', ''));
        }

        else {
          this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
        }
      }
    }
  };
</script>
